import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { useAdminMe } from '../components/RequireAdmin'
import { adminApi, can, type DatasetFilters } from '../lib/adminApi'

/**
 * **해결카드 기준 매칭** — 「이 기업 카드가 어떤 시민문제와 얼마나 맞나」(관리자UX N-2).
 *
 * 매칭 화면(`/admin/match`)은 **시민문제 기준**이라 카드 쪽에서 거꾸로 볼 길이 없었다.
 * 발주기관이 09-18에 «해봄지도에서는 하나씩 확인해야 해서요»라고 한 것이 그 자리이고,
 * 그때는 스크립트로 엑셀을 만들어 우회했다. 이 화면이 그 우회를 없앤다.
 *
 * **한 행은 후보 하나**(해결카드 × 시민문제)다. 목록·요약·내려받기가 **같은 조건**을 쓴다 —
 * 서버가 한 빌더(`lib/dataset-query.ts`)로 만들기 때문에 화면 건수와 파일 건수가 어긋나지 않는다.
 */

const GRADE_LABEL: Record<string, string> = { high: '높음', mid: '중간', low: '낮음' }

/** 빈 문자열은 「조건 없음」이라 서버에 보내지 않는다 — `?grade=`를 보내면 400이 난다. */
function clean(f: Record<string, string>): DatasetFilters {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(f)) if (v !== '') out[k] = v
  return out
}

export function AdminMatchByCardPage() {
  const me = useAdminMe()
  const [q, setQ] = useState('')
  const [grade, setGrade] = useState('')
  const [minScore, setMinScore] = useState('')
  const [includeExcluded, setIncludeExcluded] = useState(false)
  const [offset, setOffset] = useState(0)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const LIMIT = 50
  const filters = clean({
    q,
    grade,
    minScore,
    ...(includeExcluded ? { includeExcluded: 'true' } : {}),
  })
  // limit·offset은 숫자 축이다(`DatasetFilters`) — 문자열로 넣으면 타입이 막는다.
  const listQuery: DatasetFilters = { ...filters, limit: LIMIT, offset }

  const matches = useQuery({
    queryKey: ['admin-solution-matches', listQuery],
    queryFn: () => adminApi.solutionMatches(listQuery),
  })

  const mayExport =
    can(me.data, 'export', 'all') || can(me.data, 'export', 'no_pii')

  /** 조건을 바꾸면 첫 쪽으로 돌아간다 — 3쪽에서 조건만 바꾸면 빈 화면이 나온다. */
  function patch(set: () => void) {
    set()
    setOffset(0)
  }

  async function download(format: 'csv' | 'xlsx') {
    setDownloading(true)
    setDownloadError(null)
    try {
      await adminApi.exportDataset('solution-matches', filters, format, false)
    } catch {
      setDownloadError('내려받기에 실패했습니다. 조건을 좁혀 다시 시도해 주세요.')
    } finally {
      setDownloading(false)
    }
  }

  const summary = matches.data?.summary
  const total = matches.data?.total ?? 0

  return (
    <div>
      <Link className="back-link" to="/admin/match">
        ← 매칭 (시민문제 기준)
      </Link>

      <div className="admin-bar">
        <div>
          <h1 className="page-title">해결카드 기준 매칭</h1>
          <p className="page-desc">
            기업 해결카드마다 <b>어떤 시민문제와 얼마나 맞는지</b>를 봅니다. 한 줄이 후보 하나이고,
            화면에 보이는 목록과 내려받는 파일은 <b>같은 조건</b>으로 만들어집니다.
          </p>
        </div>
      </div>

      <section className="card-block">
        <div className="filter-row">
          <label className="field">
            <span>검색어</span>
            <input
              type="search"
              value={q}
              placeholder="기업명·카드·시민문제"
              onChange={(e) => patch(() => setQ(e.target.value))}
            />
          </label>
          <label className="field">
            <span>등급</span>
            <select value={grade} onChange={(e) => patch(() => setGrade(e.target.value))}>
              <option value="">전체</option>
              <option value="high">높음</option>
              <option value="mid">중간</option>
              <option value="low">낮음</option>
            </select>
          </label>
          <label className="field">
            <span>최소 적합도</span>
            <input
              type="number"
              min={0}
              max={100}
              value={minScore}
              placeholder="예: 40"
              onChange={(e) => patch(() => setMinScore(e.target.value))}
            />
          </label>
          <label className="field">
            <span>제외된 후보</span>
            <select
              value={includeExcluded ? 'true' : ''}
              onChange={(e) => patch(() => setIncludeExcluded(e.target.value === 'true'))}
            >
              <option value="">숨김</option>
              <option value="true">함께 보기</option>
            </select>
          </label>
        </div>

        {mayExport ? (
          <div className="export-bar">
            <button className="btn" disabled={downloading} onClick={() => void download('csv')}>
              CSV 내려받기
            </button>
            <button
              className="btn ghost"
              disabled={downloading}
              onClick={() => void download('xlsx')}
            >
              엑셀(XLSX) 내려받기
            </button>
            <span className="muted small">
              지금 걸린 조건 그대로 받습니다. 내려받기는 누가·무엇을·몇 건 받았는지 기록에 남습니다.
            </span>
          </div>
        ) : (
          <p className="muted small">
            이 계정에는 내려받기 권한이 없습니다(<b>export</b> 권한 필요).
          </p>
        )}
        {downloadError !== null && (
          <p className="error" role="alert">
            {downloadError}
          </p>
        )}
      </section>

      {summary !== undefined && (
        <section className="card-block">
          <h2>이 조건의 요약</h2>
          <p className="muted small">
            후보 <b>{summary.total}건</b> · 해결카드 {summary.solutionCardCount}장 · 기업{' '}
            {summary.orgCount}곳
            {summary.excluded > 0 && <> · 제외 {summary.excluded}건</>}
          </p>
          <p className="muted small">
            높음 {summary.byGrade.high} · 중간 {summary.byGrade.mid} · 낮음 {summary.byGrade.low}
            {summary.maxScore !== null && (
              <>
                {' '}
                · 최고 {summary.maxScore}점
                {summary.avgScore !== null && <> · 평균 {summary.avgScore}점</>}
              </>
            )}
          </p>
        </section>
      )}

      <QueryState isPending={matches.isPending} error={matches.error}>
        <section className="card-block">
          <div className="list-head">
            <span className="muted small">
              전체 {total}건 중 {total === 0 ? 0 : offset + 1}~
              {Math.min(offset + LIMIT, total)}
            </span>
            <span>
              <button
                className="btn ghost small-btn"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
              >
                이전
              </button>{' '}
              <button
                className="btn ghost small-btn"
                disabled={offset + LIMIT >= total}
                onClick={() => setOffset(offset + LIMIT)}
              >
                다음
              </button>
            </span>
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>기업</th>
                <th>해결카드</th>
                <th>시민문제</th>
                <th>적합도</th>
                <th>등급</th>
                <th>상태</th>
              </tr>
            </thead>
            <tbody>
              {(matches.data?.items ?? []).map((m) => (
                <tr key={m.id} className={m.excluded ? 'is-muted' : undefined}>
                  <td>
                    <Link to={`/admin/orgs/${m.org.id}`}>{m.org.name}</Link>
                  </td>
                  <td>
                    <Link to={`/admin/cards/${m.solutionCard.id}`}>{m.solutionCard.title}</Link>
                  </td>
                  <td>
                    <Link to={`/admin/problems/${m.problemCard.id}`}>{m.problemCard.title}</Link>
                  </td>
                  <td>{m.fitScore ?? '—'}</td>
                  <td>
                    {m.fitGrade === null ? '—' : (GRADE_LABEL[m.fitGrade] ?? m.fitGrade)}
                    {m.excluded && <span className="muted small"> (제외)</span>}
                  </td>
                  <td>{m.statusLabel}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {total === 0 && (
            <p className="muted small">
              조건에 맞는 후보가 없습니다. 등급·최소 적합도를 풀어 보세요.
            </p>
          )}
        </section>
      </QueryState>
    </div>
  )
}
